import { CategoryOutlined } from "@mui/icons-material";
import { Autocomplete, Avatar, Box, Collapse, InputAdornment, ListItem, List as MuiList, Rating, TextField, Typography } from "@mui/material";
import { useGetList } from "react-admin";
import { useSearchParams } from "react-router-dom";
import { ColorInput } from "../../components";
import { SortButton } from "../../components/sort-button";
import { useWebsite } from "../../hooks";
import { imageURL } from "../../http";
import { MenuGroup } from "../../layout/menu-group";
import type { CategoryTree, Filter } from "../../models";
import { hexToRgb } from "../../util";
import { SpecGroup } from "./SpecGroup";

const padding = { padding: "8px 8px" };

export const OfferFilters = ({ tree, activeBrands, open = false }: OfferFiltersProps): JSX.Element => {
    const [q, setQuery] = useSearchParams();

    const query = Object.fromEntries(q);
    const { accountId, brands } = useWebsite();

    const { data, isSuccess, isFetching } = useGetList<Filter>(
        "filter",
        {
            filter: { accountId, categoryId: tree?.categoryId, "select@": "*,...specification(name,unit)" },
        },
        { enabled: open && !!tree?.categoryId }
    );
    const setValue = (name: string, value: string) => {
        if(value) {
            setQuery({ ...query, [name]: value });
        } else {
            const { [name]: prop, ...rest } = query;
            setQuery(rest);
        }
    }
    return (
        <MuiList sx={{ padding: 0 }}>
            <SortButton />
            {tree && tree.children.entries().toArray().map(([name, { categoryId, categories, image }]) => {
                return (
                    <Collapse component={ListItem} sx={{ padding: 0, paddingRight: 1 }} key={categoryId} in={!isFetching}>
                        <MenuGroup
                            sx={{ padding }}
                            indicator
                            open={true}
                            name={name?.split(">").pop() ?? ""}
                            icon={
                                <Avatar
                                    slotProps={{ img: { sx: { objectFit: "contain" } } }}
                                    sx={t => ({ width: 32, height: 32, color: t.palette.secondary.main, backgroundColor: "transparent" })}
                                    src={image ? imageURL(image) : undefined}
                                    variant="rounded"
                                    alt={name}
                                >
                                    <CategoryOutlined />
                                </Avatar>
                            }
                            onClick={() => { 
                                setValue("categories", `${categories.values().toArray().join("-")}`);
                            }}
                        />
                    </Collapse>
                );
            })}
            <ListItem key="brands" sx={{ padding }}>
                <Autocomplete
                    multiple
                    options={brands.filter(x => tree?.brands?.has(x.brandId))}
                    renderOption={(props, record) => (
                        <Box sx={{ display: "flex", alignItems: "center" }} component="li" {...props}>
                            <Avatar variant="rounded" alt={record.name} sx={{ mr: 1, flexShrink: 0 }} src={record.image ? imageURL(record.image) : ""}>
                                {record.name[0]}
                            </Avatar>
                            {record.name}
                        </Box>
                    )}
                    getOptionLabel={record => record.name}
                    fullWidth
                    filterSelectedOptions
                    value={brands.filter(x => activeBrands.includes(x.brandId!))}
                    onChange={(_, val) => {
                        setValue("brands", `${val.map(x => x.brandId).join("-")}`);
                    }}
                    // slotProps={{ chip: { avatar: <Avatar src=""/> } }}
                    renderInput={params => <TextField {...params} label="Brands" placeholder="Brands" />}
                />
            </ListItem>
            <ListItem sx={{ padding }}>
                <Box sx={_ => ({ display: "flex", width: "100%", padding: 1, border: `1px solid rgba(var(--mui-palette-common-onBackgroundChannel) / 0.23)`, borderRadius: 1 })}>
                    <Typography component="legend" color="textSecondary" sx={{ paddingLeft: 1, width: "52%" }}>
                        Rating
                    </Typography>
                    <Rating
                        sx={t => ({ color: t.palette.secondary.main })}
                        max={4}
                        precision={0.5}
                        value={query.rating ? Number.parseInt(query.rating) : null}
                        onChange={(_, val) => {
                            setValue("rating", `${val}`);
                        }}
                    />
                </Box>
            </ListItem>
            <ListItem key="color" sx={{ padding }}>
                <ColorInput
                    helperText={false}
                    source="color"
                    onChange={(e) => {
                        setValue("color", hexToRgb(e.target.value).join("-"));
                    }}
                />
            </ListItem>
            <ListItem sx={{ padding }}>
                <TextField
                    label="Min"
                    inputMode="numeric"
                    value={query.min}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
                    sx={{ "& .MuiInputBase-root": { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}
                    onKeyUp={e => {
                        if (e.key === "Enter") {
                            // @ts-expect-error
                            setValue("min", e.target.value);
                        }
                    }}
                />
                <TextField
                    label="Max"
                    inputMode="numeric"
                    value={query.max}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
                    sx={{ "& .MuiInputBase-root": { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
                    onKeyUp={e => {
                        if (e.key === "Enter") {
                            // @ts-expect-error
                            setValue("max", e.target.value);
                        }
                    }}
                />
            </ListItem>
            {isSuccess &&
                tree?.categoryId && <SpecGroup group={"Filters"} spec={data} categoryId={tree.categoryId} setValue={setValue}/>}
        </MuiList>
    );
};

interface OfferFiltersProps {
    tree?: CategoryTree;
    activeBrands: number[];
    open?: boolean;
}


